import './i18n'
import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { useCustomEventListener, emitCustomEvent } from 'react-custom-events'
import Store from './store'
import {
  ActiveBookings,
  BookingModuleWrapper,
  PaymentModal,
  LoginModule,
  LoginStatus,
  Map,
  StatusMessages,
  ProfileModule,
  DebugPanel,
  EmbedHeader
} from '~/components'
import { qs, qsa } from '~/utils'

const MODERN = {
  allowHideMap: true // This variable is true for all booking module views except locations
}

const apps = [
  // Embed header (only in embed mode)
  ...qsa('#embed-header').map(el => {
    return { el, component: EmbedHeader }
  }),
  // Booking module
  ...qsa('.booking-module-modern-form').map(el => {
    return { el, component: BookingModuleWrapper }
  }),
  // Cashier
  ...qsa('#payment-modal').map(el => {
    return { el, component: PaymentModal }
  }),
  // Profile module
  ...qsa('#profile-module').map(el => {
    return { el, component: ProfileModule }
  }),
  // Login module
  ...qsa('#login-module').map(el => {
    return { el, component: LoginModule }
  }),
  // Login status
  ...qsa('.login-status-modern').map(el => {
    return { el, component: LoginStatus }
  }),
  // Map
  ...qsa('#map-modern').map(el => {
    return { el, component: Map }
  }),
  // Status messages
  ...qsa('#status-messages').map(el => {
    return { el, component: StatusMessages }
  }),
  // Active orders
  ...qsa('#active-bookings').map(el => {
    return {
      el,
      component: ActiveBookings,
      props: {
        slideableContent: qs('.slideable-content'),
        header: qs('.header')
      }
    }
  }),
]

// Add debug panel if in dev
if (process.env.NODE_ENV !== 'production') {
  const dp = qs('#debug-panel')
  if (dp) apps.push({ el: dp, component: DebugPanel })
}

const rootElement = createRoot(document.getElementById('reactStore'))
rootElement.render(<Store apps={apps} />)

const bmw = qs('.booking-module-modern')
if (bmw && typeof APP !== 'undefined') {
  const hasCover = bmw.classList.contains('has-cover')
  const isFullscreen = bmw.classList.contains('fullscreen')
  // The embed markup ships with .fullscreen already applied and stays that way,
  // so END_FULLSCREEN must not strip it back off.
  const lockFullscreen = !!window.EMBED_CONFIG?.isEmbed
  APP.on(APP.events.BOOKING_MODULE_FULLSCREEN, () => {
    console.log('FULLSCREEN', APP.events.BOOKING_MODULE_FULLSCREEN);
    bmw.classList.add('fullscreen');
    document.body.classList.add('booking-fullscreen');
  });
  APP.on(APP.events.BOOKING_MODULE_END_FULLSCREEN, () => {
    console.log('END FULLSCREEN', APP.events.BOOKING_MODULE_END_FULLSCREEN);
    if (lockFullscreen) return;
    bmw.classList.remove('fullscreen');
    document.body.classList.remove('booking-fullscreen');
  });
  if (hasCover) {
    APP.on(APP.events.BOOKING_MODULE_HIDE_COVER, () => bmw.classList.remove('cover-visible'))
    APP.on(APP.events.BOOKING_MODULE_SHOW_COVER, () => bmw.classList.add('cover-visible'))
    APP.on(APP.events.BOOKING_MODULE_DISABLE_HIDE_MAP, () => {
      MODERN.allowHideMap = false
    })
    APP.on(APP.events.BOOKING_MODULE_ENABLE_HIDE_MAP, () => {
      MODERN.allowHideMap = true
    })

    const mapElement = document.getElementById('map-modern')
    if (mapElement) {
      mapElement.addEventListener('click', () => {
        if (MODERN.allowHideMap) {
          APP.trigger(APP.events.BOOKING_MODULE_SHOW_COVER)
          APP.trigger(APP.events.BOOKING_MODULE_CONTRACTED)
        }
      })
    }

    const formElement = document.querySelector('.booking-module-modern-form')
    if (formElement) {
      formElement.addEventListener('click', () => {
        APP.trigger(APP.events.BOOKING_MODULE_HIDE_COVER)
      })
    }

  }
}

// ============================================
// Dynamic Booking Module for Edit / Return Trip Mode
// ============================================
// When EDIT_BOOKING or RETURN_TRIP_BOOKING is triggered on pages without a
// booking module, dynamically create and mount one as a fullscreen overlay

let dynamicBookingModuleRoot = null
let dynamicMapRoot = null
let dynamicBookingModuleContainer = null
let pendingBookingEvent = null // { name, data } re-emitted once the module has mounted

const createDynamicBookingModule = (eventName, bookingData) => {
  // Don't create if booking module already exists on the page
  if (qs('.booking-module-modern-form')) {
    return false
  }

  console.log(`Creating dynamic booking module overlay for ${eventName}`)

  // Store the event to re-emit after mount
  pendingBookingEvent = { name: eventName, data: bookingData }

  // Set global flag for dynamic overlay mode
  window.DYNAMIC_OVERLAY_MODE = true

  // Create overlay container
  const overlay = document.createElement('div')
  overlay.id = 'dynamic-booking-overlay'
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    background: white;
    overflow-y: auto;
  `

  // Create booking module structure (matching booking_module_modern.twig)
  const bookingModuleContainer = document.createElement('div')
  bookingModuleContainer.className = 'booking-module-modern fullscreen'

  const formContainer = document.createElement('div')
  formContainer.className = 'booking-module-modern-form modern-component'

  const mapContainer = document.createElement('div')
  mapContainer.id = 'map-modern'

  bookingModuleContainer.appendChild(formContainer)
  bookingModuleContainer.appendChild(mapContainer)
  overlay.appendChild(bookingModuleContainer)
  document.body.appendChild(overlay)

  // Store references
  dynamicBookingModuleContainer = overlay

  // Mount React app with BookingModuleWrapper
  const reactRoot = createRoot(formContainer)
  dynamicBookingModuleRoot = reactRoot

  // Mount Map component on the map container
  const mapRoot = createRoot(mapContainer)
  dynamicMapRoot = mapRoot

  // Create a wrapper component to handle cleanup
  const DynamicBookingWrapper = () => {
    useCustomEventListener('RESET_FORM', () => {
      // When booking is closed/reset, remove the dynamic overlay
      console.log('Closing dynamic booking module')
      setTimeout(() => removeDynamicBookingModule(), 100)
    })

    // Re-emit the event after the component mounts
    useEffect(() => {
      if (pendingBookingEvent) {
        const { name, data } = pendingBookingEvent
        console.log(`Re-emitting ${name} for mounted dynamic module`)
        setTimeout(() => {
          emitCustomEvent(name, data)
          pendingBookingEvent = null
        }, 100)
      }
    }, [])

    return <BookingModuleWrapper />
  }

  reactRoot.render(
    <Store apps={[]} nested>
      <DynamicBookingWrapper />
    </Store>
  )

  // Render the Map component
  mapRoot.render(<Map />)

  return true
}

const removeDynamicBookingModule = () => {
  if (dynamicBookingModuleRoot) {
    dynamicBookingModuleRoot.unmount()
    dynamicBookingModuleRoot = null
  }
  if (dynamicMapRoot) {
    dynamicMapRoot.unmount()
    dynamicMapRoot = null
  }
  if (dynamicBookingModuleContainer) {
    dynamicBookingModuleContainer.remove()
    dynamicBookingModuleContainer = null
  }
  pendingBookingEvent = null

  // Clear dynamic overlay flag
  window.DYNAMIC_OVERLAY_MODE = false
}

// Set up a global listener component that mounts immediately
// This listens for EDIT_BOOKING / RETURN_TRIP_BOOKING events even when the booking module isn't present
const GlobalEditBookingListener = () => {
  const onBookingEvent = (eventName) => (bookingData) => {
    console.log(`[Dynamic Booking] ${eventName} event received:`, bookingData)
    console.log('[Dynamic Booking] Booking module exists?', !!qs('.booking-module-modern-form'))

    // Check if booking module doesn't exist
    if (!qs('.booking-module-modern-form')) {
      console.log('[Dynamic Booking] No booking module found - creating dynamic overlay')
      createDynamicBookingModule(eventName, bookingData)
    } else {
      console.log('[Dynamic Booking] Booking module already exists, skipping overlay creation')
    }
  }
  useCustomEventListener('EDIT_BOOKING', onBookingEvent('EDIT_BOOKING'), [])
  useCustomEventListener('RETURN_TRIP_BOOKING', onBookingEvent('RETURN_TRIP_BOOKING'), [])

  return null // This component doesn't render anything
}

// Mount the global listener immediately
if (typeof window !== 'undefined') {
  console.log('[Dynamic Booking] Setting up global EDIT_BOOKING listener')
  const listenerRoot = createRoot(document.createElement('div'))
  listenerRoot.render(<GlobalEditBookingListener />)
  console.log('[Dynamic Booking] Listener setup complete')
}
